// A bounded, non-destructive pixel operation shared by the canvas UI and tests.
// Tolerance is RGB Euclidean distance, not a percentage. Softness is 0–3 pixels
// at the working image resolution; neither operation changes RGB values.
export const CONNECTED_BACKGROUND = Object.freeze({ maxEdge: 1400, tolerance: 15, maxTolerance: 60, softness: 1, maxSoftness: 3 });

const invalidImage = () => new Error('图片尺寸或像素数据无效。');
const invalidOptions = () => new Error('去底参数无效，请重试。');

function imageBounds(image) {
  const { width, height, data } = image || {};
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) throw invalidImage();
  if (width > CONNECTED_BACKGROUND.maxEdge || height > CONNECTED_BACKGROUND.maxEdge) throw new Error('图片尺寸过大，请使用最长边不超过 1400 像素的工作图片。');
  if (!(data instanceof Uint8ClampedArray || data instanceof Uint8Array) || data.length !== width * height * 4) throw invalidImage();
  return { width, height, data };
}

const distanceSquared = (data, pixel, rgb) => {
  const offset = pixel * 4;
  return (data[offset] - rgb[0]) ** 2 + (data[offset + 1] - rgb[1]) ** 2 + (data[offset + 2] - rgb[2]) ** 2;
};

function borderPixels(width, height) {
  const indices = [];
  for (let x = 0; x < width; x++) indices.push(x);
  if (height > 1) for (let x = 0; x < width; x++) indices.push((height - 1) * width + x);
  for (let y = 1; y < height - 1; y++) {
    indices.push(y * width);
    if (width > 1) indices.push(y * width + width - 1);
  }
  return indices;
}

function automaticColor(data, border, tolerance) {
  const opaque = border.filter(pixel => data[pixel * 4 + 3] >= 16);
  if (opaque.length < border.length / 2) throw new Error('图片已经有透明背景，可直接使用或点选继续去底。');
  const bins = new Map();
  for (const pixel of opaque) {
    const offset = pixel * 4;
    const key = (data[offset] >> 4) * 256 + (data[offset + 1] >> 4) * 16 + (data[offset + 2] >> 4);
    const bin = bins.get(key) || { count: 0, sum: [0, 0, 0] };
    bin.count++;
    for (let channel = 0; channel < 3; channel++) bin.sum[channel] += data[offset + channel];
    bins.set(key, bin);
  }
  // Inspect only eight candidates, then count actual nearby edge pixels. This
  // tolerates JPEG noise and quantization boundaries without a quadratic scan.
  let best = null;
  const confidenceLimit = Math.max(3, tolerance) ** 2;
  for (const bin of [...bins.values()].sort((a, b) => b.count - a.count).slice(0, 8)) {
    const mean = bin.sum.map(sum => sum / bin.count);
    let nearest = opaque[0], nearestDistance = Infinity;
    for (const pixel of opaque) {
      const distance = distanceSquared(data, pixel, mean);
      if (distance < nearestDistance) { nearest = pixel; nearestDistance = distance; }
    }
    const rgb = Array.from(data.subarray(nearest * 4, nearest * 4 + 3));
    const count = opaque.reduce((sum, pixel) => sum + (distanceSquared(data, pixel, rgb) <= confidenceLimit ? 1 : 0), 0);
    if (!best || count > best.count) best = { rgb, count };
  }
  if (!best || best.count < opaque.length * .6) throw new Error('无法确定背景颜色，请点选要去除的背景。');
  return best.rgb;
}

export function removeConnectedPixels(image, options = {}) {
  const { width, height, data } = imageBounds(image);
  if (!options || typeof options !== 'object') throw invalidOptions();
  const { seed = null, tolerance = CONNECTED_BACKGROUND.tolerance, softness = CONNECTED_BACKGROUND.softness } = options;
  if (!Number.isFinite(tolerance) || tolerance < 0 || tolerance > CONNECTED_BACKGROUND.maxTolerance || !Number.isInteger(softness) || softness < 0 || softness > CONNECTED_BACKGROUND.maxSoftness) throw invalidOptions();
  if (seed !== null && (!Number.isFinite(seed?.x) || !Number.isFinite(seed?.y) || seed.x < 0 || seed.y < 0 || seed.x >= width || seed.y >= height)) throw new Error('请点选图片范围内的背景。');

  const count = width * height;
  let visiblePixels = 0;
  for (let pixel = 0; pixel < count; pixel++) if (data[pixel * 4 + 3]) visiblePixels++;
  if (!visiblePixels) throw new Error('图片已完全透明，请撤销或重新上传。');
  const border = seed === null ? borderPixels(width, height) : null;
  const seedPixel = seed === null ? null : Math.floor(seed.y) * width + Math.floor(seed.x);
  if (seedPixel !== null && data[seedPixel * 4 + 3] < 16) throw new Error('这里是透明区域，请点选有颜色的位置。');
  const rgb = border ? automaticColor(data, border, tolerance) : Array.from(data.subarray(seedPixel * 4, seedPixel * 4 + 3));
  const limitSquared = tolerance ** 2;
  const state = new Uint8Array(count), queue = new Int32Array(count);
  let head = 0, tail = 0;
  const visit = pixel => {
    if (state[pixel]) return;
    state[pixel] = 2; // examined, but not connected background
    if (!data[pixel * 4 + 3] || distanceSquared(data, pixel, rgb) > limitSquared) return;
    state[pixel] = 1;
    queue[tail++] = pixel;
  };
  if (border) border.forEach(visit); else visit(seedPixel);
  while (head < tail) {
    const pixel = queue[head++], x = pixel % width;
    if (x > 0) visit(pixel - 1);
    if (x < width - 1) visit(pixel + 1);
    if (pixel >= width) visit(pixel - width);
    if (pixel < count - width) visit(pixel + width);
  }
  if (tail === visiblePixels) throw new Error('去底会清除整张图片，请降低容差或重新点选背景。');
  if (!tail) throw new Error('没有找到可去除的背景，请重新点选。');
  const removedPixels = tail, result = new Uint8ClampedArray(data);
  for (let index = 0; index < removedPixels; index++) result[queue[index] * 4 + 3] = 0;

  // Feather only a narrow, similar-colored fringe. Never flood through these
  // partial pixels and never soften disconnected pixels that match the seed:
  // a same-colored eye/highlight enclosed by the subject stays unchanged.
  const softBand = 12, softLimitSquared = (tolerance + softBand) ** 2;
  let softenedPixels = 0;
  const soften = (pixel, depth) => {
    if (state[pixel] === 1 || state[pixel] >= 3 || !data[pixel * 4 + 3]) return;
    const distance = distanceSquared(data, pixel, rgb);
    if (distance <= limitSquared || distance >= softLimitSquared) return;
    const colorAlpha = Math.max(.25, (Math.sqrt(distance) - tolerance) / softBand);
    const factor = 1 - (1 - colorAlpha) * (1 - (depth - 1) / softness);
    const alpha = Math.round(data[pixel * 4 + 3] * factor);
    if (alpha >= data[pixel * 4 + 3]) return;
    result[pixel * 4 + 3] = alpha;
    state[pixel] = depth + 2;
    queue[tail++] = pixel;
    softenedPixels++;
  };
  if (softness) {
    head = 0;
    while (head < tail) {
      const pixel = queue[head++], depth = state[pixel] === 1 ? 1 : state[pixel] - 1;
      if (depth > softness) continue;
      const x = pixel % width;
      if (x > 0) soften(pixel - 1, depth);
      if (x < width - 1) soften(pixel + 1, depth);
      if (pixel >= width) soften(pixel - width, depth);
      if (pixel < count - width) soften(pixel + width, depth);
    }
  }
  return { data: result, removedPixels, softenedPixels };
}
